import { Box, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { SettingForm } from 'components/common/settings-dialog/form';
import { SettingRow } from 'components/common/settings-dialog/row';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IPitchListPutManyRequest } from 'lib_ts/interfaces/pitches';
import { clone } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { AdminPitchListsService } from 'services/admin/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';

interface IProps {
  onClose: () => void;
}

export const UserSectionReassignLists = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const latestRole = useLatest(current.role);
  const reassignOptions = useAuthStore((s) => s.reassignOptions);

  const [payload, setPayload] = useState(() => {
    const output: IPitchListPutManyRequest = {
      filter: {
        _parent_id: current.userID,
        _parent_def: PitchListOwner.User,
      },
      update: {
        processed: new Date(),
        process_notes: `reassigned from settings user section by ${current.email}`,
      },
    };

    return output;
  });

  // automatically reset id options and reset parent ID
  useEffect(() => {
    setIdOptions(
      PitchListHelper.getReassignToIdOptions({
        options: reassignOptions,
        role: current.role,
        teamID: current.teamID,
        parentDef: payload.filter._parent_def ?? '',
        parentId: '',
      })
    );
  }, [reassignOptions, current, payload.filter._parent_def]);

  const defOptions = useMemo<IOption[]>(() => {
    const output: IOption[] = [];

    if (reassignOptions.users.length > 0) {
      output.push({
        label: 'common.personal',
        value: PitchListOwner.User,
      });
    }

    if (current.machine_lists && reassignOptions.machines.length > 0) {
      output.push({
        label: 'common.machine',
        value: PitchListOwner.Machine,
      });
    }

    if (current.team_lists && reassignOptions.teams.length > 0) {
      output.push({
        label: 'common.team',
        value: PitchListOwner.Team,
      });
    }

    return output;
  }, [reassignOptions, current]);

  const [idOptions, setIdOptions] = useState<IOption[]>(
    PitchListHelper.getReassignToIdOptions({
      options: reassignOptions,
      role: current.role,
      teamID: current.teamID,
      parentDef: payload.filter._parent_def ?? '',
      parentId: payload.filter._parent_id ?? '',
    })
  );

  // when user selects a new parent ID, try to submit
  useEffect(() => {
    const callback = async () => {
      if (!payload.update._parent_def) {
        console.warn('Skipping submit: no parent def');
        return;
      }

      if (!payload.filter._parent_id) {
        console.warn('Skipping submit: no parent ID');
        return;
      }

      switch (latestRole.current) {
        case UserRole.team_admin:
        case UserRole.admin: {
          /** run the update via admin service -- can only bulk reassign things belonging to own team as team admin */
          const success =
            await AdminPitchListsService.getInstance().updateMany(payload);

          if (!success) {
            return;
          }

          await useListLibraryStore.getState().refreshLists(true);

          props.onClose();
          return;
        }

        case UserRole.basic: {
          // proceed as a regular user

          // ? not copying -- check permissions to overwrite/modify
          if (
            payload.filter._parent_def !== PitchListOwner.User ||
            payload.filter._parent_id !== current.userID
          ) {
            NotifyHelper.warning({
              message_md:
                'You cannot reassign pitch lists that do not belong to you as a regular user.',
            });
            return;
          }

          /** run the update via regular pitch list service -- can only bulk reassign things belonging to self as user */
          const success = await useListLibraryStore
            .getState()
            .updateLists(payload);

          if (!success) {
            return;
          }

          await useListLibraryStore.getState().refreshLists(true);

          props.onClose();
          return;
        }

        default: {
          return;
        }
      }
    };

    callback();
  }, [payload.filter]);

  return (
    <SettingForm>
      <Box>{t('settings.reassign-lists-msg')}</Box>

      <SettingRow
        header="common.type"
        description={
          <Text>
            <p>{t('settings.reassign-type-msg')}</p>
            <p>{t('settings.reassign-type-example-msg')}</p>
          </Text>
        }
        input={
          <CommonSelectInput
            id="reassign-parent-def"
            name="_parent_def"
            options={defOptions}
            value={payload.update._parent_def}
            onChange={(v) => {
              setPayload((prev) => {
                const nextPayload = clone(prev);

                nextPayload.update = {
                  _parent_def: v as PitchListOwner,
                  _parent_id: undefined,
                };

                return nextPayload;
              });
            }}
            optional
          />
        }
      />

      <SettingRow
        header="common.owner"
        description={t('settings.reassign-owner-msg')}
        input={
          <CommonSearchInput
            id="reassign-parent"
            name="_parent_id"
            disabled={!payload.update._parent_def}
            options={idOptions}
            values={
              payload.update._parent_id ? [payload.update._parent_id] : []
            }
            onChange={(v) => {
              const parentId = v[0];

              if (!parentId) {
                NotifyHelper.warning({
                  message_md: t('common.check-inputs-msg'),
                });
                return;
              }

              setPayload((prev) => {
                const nextPayload = clone(prev);
                nextPayload.update._parent_id = parentId;
                return nextPayload;
              });
            }}
            optional
          />
        }
      />
    </SettingForm>
  );
};
